import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiGiphyService } from '@fi-shared/fi-giphy/fi-giphy.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiGiphySize } from '@fi-shared/fi-giphy/fi-giphy-size';
import { ITextEditorLinkAttachment } from '@fi-shared/fi-text-editor/fi-text-editor';
import { TextEditorAttachmentType } from '@fi-shared/fi-text-editor/fi-text-editor-attachment/text-editor-attachment-type';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { IGiphyItem } from '@fi-shared/fi-giphy/fi-giphy';

@Component({
    selector: 'fi-insert-giphy',
    template: `
        <div class="gif-search-container">
          <mat-form-field class="gif-search-input ContrastBorderColor" appearance="outline">
            <input
              matInput
              #searchInput
              [placeholder]="'gif.search' | fiAppText"
              [ngModel]="searchText"
              (ngModelChange)="onSearchTextChange($event)"
              />
            </mat-form-field>
            <button class="close-button" mat-icon-button (click)="close()">
              <mat-icon>
                <span class="fa fa-times"></span>
              </mat-icon>
            </button>
          </div>
        
          <div class="gifs-container" (fiWhenScrolled)="onScroll()">
            @for (item of giphyItems$ | async; track item) {
              <div class="gif-item" (click)="onGiphyItemClick(item)">
                <img alt="" class="gif" [src]="item.url" />
              </div>
            }
            @if (loading$ | async) {
              <div class="text-center">
                <fi-loading class="small"></fi-loading>
              </div>
            }
          </div>
        `,
    styleUrls: ['./fi-insert-giphy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-insert-giphy' },
    standalone: false,
})
export class FiInsertGiphyComponent implements OnInit, OnDestroy {
    @Input() giphySize: FiGiphySize = FiGiphySize.fixed_height_small;
    @Output() giphyItemClick: EventEmitter<ITextEditorLinkAttachment> = new EventEmitter<ITextEditorLinkAttachment>();
    @Output() onClose: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild('searchInput', { static: true }) searchInput: ElementRef<HTMLInputElement>;
    @HostBinding('class.mobile-layout') get isMobilePhone(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    searchText: string = '';
    searchTextChanged: Subject<string> = new Subject<string>();
    giphyItems$: BehaviorSubject<IGiphyItem[]> = new BehaviorSubject<IGiphyItem[]>([]);
    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly searchDebounceTimeMS: number = 1000;

    constructor(
        private giphyService: FiGiphyService,
        private elementRef: ElementRef,
        private detectDeviceService: DetectDevicesService,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {}

    public ngOnInit(): void {
        if (!this.isMobilePhone)
            this.fiBrowserEventsService
                .getWindowEventSubject$('click')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(($event: MouseEvent) => {
                    if (!this.elementRef.nativeElement.contains($event.target)) this.close();
                });

        this.searchTextChanged
            .pipe(debounceTime(this.searchDebounceTimeMS), distinctUntilChanged())
            .subscribe((newSearchText: string) => {
                this.searchText = newSearchText;
                this.giphyItems$.next([]);
                this.resetOffsets();
                this.searchText.trim().length ? this.search() : this.loadTrendingGIFs();
            });
        this.loadTrendingGIFs();
        this.searchInput.nativeElement.focus();
    }

    public onScroll(): void {
        this.searchText.trim().length ? this.search() : this.loadTrendingGIFs();
    }

    public onSearchTextChange(searchText: string): void {
        this.searchTextChanged.next(searchText);
    }

    private loadTrendingGIFs(): void {
        this.loading$.next(true);
        this.giphyService
            .getTrendingGifs(this.giphySize)
            .then((result: IGiphyItem[]) => {
                this.giphyItems$.next([...this.giphyItems$.getValue(), ...result]);
            })
            .finally(() => this.loading$.next(false));
    }

    private search(): void {
        this.loading$.next(true);
        this.giphyService
            .search(this.searchText, this.giphySize)
            .then((result: IGiphyItem[]) => {
                this.giphyItems$.next([...this.giphyItems$.getValue(), ...result]);
            })
            .finally(() => this.loading$.next(false));
    }

    public onGiphyItemClick(item: IGiphyItem): void {
        const linkAttachment: ITextEditorLinkAttachment = {
            LinkType: TextEditorAttachmentType.Gif,
            Url: item.originalUrl,
        };
        this.giphyItemClick.emit(linkAttachment);
    }

    public close(): void {
        this.onClose.emit(false);
    }

    private resetOffsets(): void {
        this.giphyService.resetSearchOffset();
        this.giphyService.resetTrendingOffset();
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.resetOffsets();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
